import {
  Page,
  Layout,
  Card,
  Checkbox,
  IndexTable,
  Badge,
  Button,
  Modal,
  TextField,
  Select,
  BlockStack,
  Text,
  InlineStack,
  Icon,
  Divider,
  Tabs,
  Collapsible,
  useIndexResourceState,
} from '@shopify/polaris';
import { DeleteIcon, PlusIcon } from '@shopify/polaris-icons';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';

const TRANSLATION_LOCALES = [
  { code: 'en', name: 'English' },
  { code: 'pt', name: 'Português' },
  { code: 'fr', name: 'Français' },
  { code: 'it', name: 'Italiano' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pl', name: 'Polski' },
];

interface Survey {
  id: string;
  title: string;
  description: string | null;
  pointsReward: number;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  responseCount: number;
}

interface QuestionForm {
  id?: string;
  questionText: string;
  questionType: 'single_choice' | 'multiple_choice';
  options: string[];
}

export default function Surveys() {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Survey | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // Form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [pointsReward, setPointsReward] = useState('0');
  const [hidden, setHidden] = useState(false);
  const [pushScheduledAt, setPushScheduledAt] = useState('');
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [questions, setQuestions] = useState<QuestionForm[]>([
    { questionText: '', questionType: 'single_choice', options: ['', ''] },
  ]);

  // Translations: { locale: { field: value } }
  // For surveys: field = 'title' or 'question_<index>_text'
  const [translations, setTranslations] = useState<Record<string, Record<string, string>>>({});
  const [translationTab, setTranslationTab] = useState(0);
  const [showTranslations, setShowTranslations] = useState(false);

  function updateTranslation(locale: string, field: string, value: string) {
    setTranslations((prev) => ({
      ...prev,
      [locale]: { ...(prev[locale] ?? {}), [field]: value },
    }));
  }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet<{ surveys: Survey[] }>('/panel-api/surveys');
      setSurveys(data.surveys);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { questionText: '', questionType: 'single_choice', options: ['', ''] },
    ]);
  };

  const removeQuestion = (idx: number) => {
    setQuestions(questions.filter((_, i) => i !== idx));
  };

  const updateQuestion = (idx: number, field: keyof QuestionForm, value: any) => {
    const updated = [...questions];
    (updated[idx] as any)[field] = value;
    setQuestions(updated);
  };

  const addOption = (qIdx: number) => {
    const updated = [...questions];
    updated[qIdx].options.push('');
    setQuestions(updated);
  };

  const updateOption = (qIdx: number, oIdx: number, value: string) => {
    const updated = [...questions];
    updated[qIdx].options[oIdx] = value;
    setQuestions(updated);
  };

  const removeOption = (qIdx: number, oIdx: number) => {
    const updated = [...questions];
    updated[qIdx].options = updated[qIdx].options.filter((_, i) => i !== oIdx);
    setQuestions(updated);
  };

  const openEdit = async (survey: Survey) => {
    setEditLoading(true);
    setEditing(survey);
    try {
      const data = await apiGet<{
        survey: any;
        questions: Array<{ id: string; questionText: string; questionType: string; options: string[]; sortOrder: number }>;
        surveyTranslations: Record<string, Record<string, string>>;
        questionTranslations: Record<string, Record<string, Record<string, string>>>;
      }>(`/panel-api/surveys/${survey.id}`);

      setTitle(data.survey.title || '');
      setDescription(data.survey.description || '');
      setPointsReward(String(data.survey.pointsReward ?? 0));
      setHidden(data.survey.hidden ?? false);
      setPushScheduledAt(data.survey.pushScheduledAt ? new Date(data.survey.pushScheduledAt).toISOString().slice(0, 16) : '');
      setPushTitle(data.survey.pushTitle || '');
      setPushBody(data.survey.pushBody || '');
      setQuestions(
        data.questions.map((q) => ({
          id: q.id,
          questionText: q.questionText,
          questionType: q.questionType as 'single_choice' | 'multiple_choice',
          options: q.options as string[],
        })),
      );

      // Rebuild translations state: { locale: { title: ..., question_0: ..., question_0_option_0: ... } }
      const trans: Record<string, Record<string, string>> = {};
      for (const [locale, fields] of Object.entries(data.surveyTranslations)) {
        if (!trans[locale]) trans[locale] = {};
        for (const [key, value] of Object.entries(fields)) {
          trans[locale][key] = value;
        }
      }
      // Question translations
      for (let qIdx = 0; qIdx < data.questions.length; qIdx++) {
        const qId = data.questions[qIdx].id;
        const qTrans = data.questionTranslations[qId] ?? {};
        for (const [locale, fields] of Object.entries(qTrans)) {
          if (!trans[locale]) trans[locale] = {};
          for (const [key, value] of Object.entries(fields)) {
            if (key === 'text') {
              trans[locale][`question_${qIdx}`] = value;
            } else if (key.startsWith('option_')) {
              trans[locale][`question_${qIdx}_${key}`] = value;
            }
          }
        }
      }
      setTranslations(trans);
      setTranslationTab(0);
      setShowTranslations(Object.keys(trans).length > 0);
      setShowModal(true);
    } catch (err) {
      console.error(err);
      alert('Error al cargar survey');
    } finally {
      setEditLoading(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setTitle('');
    setDescription('');
    setPointsReward('0');
    setHidden(false);
    setPushScheduledAt('');
    setPushTitle('');
    setPushBody('');
    setQuestions([{ questionText: '', questionType: 'single_choice', options: ['', ''] }]);
    setTranslations({});
    setTranslationTab(0);
    setShowTranslations(false);
  };

  const saveTranslations = async (surveyId: string, questionIds: string[]) => {
    for (const [locale, fields] of Object.entries(translations)) {
      const surveyFields: Record<string, string> = {};
      const questionFieldsByIdx: Record<number, Record<string, string>> = {};

      for (const [key, value] of Object.entries(fields)) {
        if (!value || !value.trim()) continue;
        const optMatch = key.match(/^question_(\d+)_option_(\d+)$/);
        const qMatch = key.match(/^question_(\d+)$/);
        if (optMatch) {
          const qIdx = parseInt(optMatch[1]);
          const oIdx = parseInt(optMatch[2]);
          if (!questionFieldsByIdx[qIdx]) questionFieldsByIdx[qIdx] = {};
          questionFieldsByIdx[qIdx][`option_${oIdx}`] = value;
        } else if (qMatch) {
          const qIdx = parseInt(qMatch[1]);
          if (!questionFieldsByIdx[qIdx]) questionFieldsByIdx[qIdx] = {};
          questionFieldsByIdx[qIdx].text = value;
        } else {
          surveyFields[key] = value;
        }
      }

      if (Object.keys(surveyFields).length > 0) {
        await apiPut(`/panel-api/translations/survey/${surveyId}`, {
          locale,
          translations: surveyFields,
        });
      }

      for (const [qIdxStr, qFields] of Object.entries(questionFieldsByIdx)) {
        const qIdx = parseInt(qIdxStr);
        const questionId = questionIds[qIdx];
        if (questionId && Object.keys(qFields).length > 0) {
          await apiPut(`/panel-api/translations/survey_question/${questionId}`, {
            locale,
            translations: qFields,
          });
        }
      }
    }
  };

  const handleSave = async () => {
    try {
      const questionsPayload = questions
        .filter((q) => q.questionText.trim())
        .map((q, idx) => ({
          ...(q.id ? { id: q.id } : {}),
          questionText: q.questionText,
          questionType: q.questionType,
          options: q.options.filter((o) => o.trim()),
          sortOrder: idx,
        }));

      if (editing) {
        // Update existing survey
        await apiPut(`/panel-api/surveys/${editing.id}`, {
          title,
          description: description || undefined,
          pointsReward: parseInt(pointsReward) || 0,
          hidden,
          pushScheduledAt: pushScheduledAt || undefined,
          pushTitle: pushTitle || undefined,
          pushBody: pushBody || undefined,
          questions: questionsPayload,
        });

        // Save translations using existing question IDs
        const questionIds = questions.map((q) => q.id || '');
        await saveTranslations(editing.id, questionIds);
      } else {
        // Create new survey
        const result = await apiPost<{ survey: { id: string; questions?: Array<{ id: string }> } }>('/panel-api/surveys', {
          title,
          description: description || undefined,
          pointsReward: parseInt(pointsReward) || 0,
          hidden,
          pushScheduledAt: pushScheduledAt || undefined,
          pushTitle: pushTitle || undefined,
          pushBody: pushBody || undefined,
          questions: questionsPayload,
        });

        const surveyId = result.survey?.id;
        const createdQuestions = result.survey?.questions ?? [];
        if (surveyId) {
          await saveTranslations(surveyId, createdQuestions.map((q) => q.id));
        }
      }

      setShowModal(false);
      resetForm();
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const handleActivate = async (id: string) => {
    await apiPost(`/panel-api/surveys/${id}/activate`);
    await fetchData();
  };

  const handleClose = async (id: string) => {
    await apiPost(`/panel-api/surveys/${id}/close`);
    await fetchData();
  };

  const handleDeleteSurvey = async (id: string) => {
    if (!confirm('Delete this survey and all responses?')) return;
    await apiDelete(`/panel-api/surveys/${id}`);
    await fetchData();
  };

  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(surveys as any);

  const rowMarkup = surveys.map((s, idx) => (
    <IndexTable.Row
      id={s.id}
      key={s.id}
      position={idx}
      selected={selectedResources.includes(s.id)}
    >
      <IndexTable.Cell>
        <InlineStack gap="200" blockAlign="center">
          <Text as="span" variant="bodyMd" fontWeight="bold">
            {s.title}
          </Text>
          {(s as any).hidden && <Badge tone="info">Journey</Badge>}
        </InlineStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Badge
          tone={
            s.status === 'active'
              ? 'success'
              : s.status === 'closed'
              ? undefined
              : 'attention'
          }
        >
          {s.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{s.pointsReward} pts</IndexTable.Cell>
      <IndexTable.Cell>{s.responseCount}</IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          {s.status === 'draft' && (
            <Button size="slim" variant="primary" onClick={() => handleActivate(s.id)}>
              Activate
            </Button>
          )}
          {s.status === 'active' && (
            <Button size="slim" onClick={() => handleClose(s.id)}>
              Close
            </Button>
          )}
          <Button size="slim" onClick={() => openEdit(s)} loading={editLoading && editing?.id === s.id}>
            Editar
          </Button>
          <Button size="slim" onClick={() => navigate(`/surveys/${s.id}/results`)}>
            Results
          </Button>
          <Button size="slim" tone="critical" onClick={() => handleDeleteSurvey(s.id)}>
            Delete
          </Button>
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page
      title="Surveys"
      primaryAction={{ content: 'Create survey', onAction: () => { resetForm(); setShowModal(true); } }}
    >
      <Layout>
        <Layout.Section>
          <Card padding="0">
            <IndexTable
              resourceName={{ singular: 'survey', plural: 'surveys' }}
              itemCount={surveys.length}
              selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
              onSelectionChange={handleSelectionChange}
              headings={[
                { title: 'Title' },
                { title: 'Status' },
                { title: 'Reward' },
                { title: 'Responses' },
                { title: 'Actions' },
              ]}
              loading={loading}
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>
      </Layout>

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? 'Editar Survey' : 'Create Survey'}
        primaryAction={{ content: editing ? 'Guardar' : 'Create', onAction: handleSave }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setShowModal(false) }]}
        size="large"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Title"
              value={title}
              onChange={setTitle}
              autoComplete="off"
            />
            <TextField
              label="Description"
              value={description}
              onChange={setDescription}
              multiline={2}
              autoComplete="off"
            />
            <TextField
              label="Points reward (0 for none)"
              type="number"
              value={pointsReward}
              onChange={setPointsReward}
              autoComplete="off"
            />
            <Checkbox
              label="Solo para Journeys (oculta del listado general)"
              checked={hidden}
              onChange={(v) => {
                setHidden(v);
                if (v) {
                  setPushScheduledAt('');
                  setPushTitle('');
                  setPushBody('');
                }
              }}
            />

            {!hidden && (
              <>
                <Divider />
                <Text as="h3" variant="headingSm">🔔 Notificación push</Text>
                <TextField
                  label="Título del push"
                  value={pushTitle}
                  onChange={setPushTitle}
                  autoComplete="off"
                  placeholder={title || 'Título de la encuesta'}
                  helpText="Si se deja vacío, se usará el título de la encuesta"
                />
                <TextField
                  label="Cuerpo del push"
                  value={pushBody}
                  onChange={setPushBody}
                  autoComplete="off"
                  placeholder="Responde y gana puntos"
                />
                <div>
                  <label style={{ fontSize: 13, display: 'block', marginBottom: 4, fontWeight: 500 }}>
                    ⏰ Programar envío
                  </label>
                  <input
                    type="datetime-local"
                    value={pushScheduledAt}
                    onChange={(e) => setPushScheduledAt(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ccc',
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: 'inherit',
                    }}
                  />
                  <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                    {pushScheduledAt
                      ? `Se enviará el ${new Date(pushScheduledAt).toLocaleString('es-ES')}`
                      : 'Deja vacío para no enviar push'}
                  </p>
                </div>
              </>
            )}

            <Divider />
            <Text as="h3" variant="headingMd">Questions</Text>

            {questions.map((q, qIdx) => (
              <Card key={qIdx}>
                <BlockStack gap="300">
                  <InlineStack align="space-between">
                    <Text as="h4" variant="headingSm">Question {qIdx + 1}</Text>
                    {questions.length > 1 && (
                      <Button
                        size="slim"
                        tone="critical"
                        icon={DeleteIcon}
                        onClick={() => removeQuestion(qIdx)}
                      />
                    )}
                  </InlineStack>
                  <TextField
                    label="Question text"
                    value={q.questionText}
                    onChange={(v) => updateQuestion(qIdx, 'questionText', v)}
                    autoComplete="off"
                  />
                  <Select
                    label="Type"
                    options={[
                      { label: 'Single choice', value: 'single_choice' },
                      { label: 'Multiple choice', value: 'multiple_choice' },
                    ]}
                    value={q.questionType}
                    onChange={(v) => updateQuestion(qIdx, 'questionType', v)}
                  />
                  <Text as="p" variant="bodySm" tone="subdued">Options</Text>
                  {q.options.map((opt, oIdx) => (
                    <InlineStack key={oIdx} gap="200" blockAlign="center">
                      <div style={{ flex: 1 }}>
                        <TextField
                          label={`Option ${oIdx + 1}`}
                          labelHidden
                          value={opt}
                          onChange={(v) => updateOption(qIdx, oIdx, v)}
                          autoComplete="off"
                        />
                      </div>
                      {q.options.length > 2 && (
                        <Button
                          size="slim"
                          tone="critical"
                          icon={DeleteIcon}
                          onClick={() => removeOption(qIdx, oIdx)}
                        />
                      )}
                    </InlineStack>
                  ))}
                  <Button size="slim" icon={PlusIcon} onClick={() => addOption(qIdx)}>
                    Add option
                  </Button>
                </BlockStack>
              </Card>
            ))}

            <Button onClick={addQuestion} icon={PlusIcon}>
              Add question
            </Button>

            <Divider />

            {/* Translations */}
            <Button
              onClick={() => setShowTranslations(!showTranslations)}
              disclosure={showTranslations ? 'up' : 'down'}
              variant="plain"
            >
              Traducciones ({String(TRANSLATION_LOCALES.length)} idiomas)
            </Button>
            <Collapsible open={showTranslations} id="survey-translations">
              <BlockStack gap="300">
                <Text as="p" variant="bodySm" tone="subdued">
                  Traduce el título de la encuesta y el texto de las preguntas. El idioma por defecto (Español) se configura arriba.
                </Text>
                <Tabs
                  tabs={TRANSLATION_LOCALES.map((l) => ({ id: l.code, content: l.name }))}
                  selected={translationTab}
                  onSelect={setTranslationTab}
                >
                  <Card>
                    <BlockStack gap="300">
                      <Text as="p" variant="bodySm" tone="subdued">
                        Referencia (ES): {title || '(sin título)'}
                      </Text>
                      <TextField
                        label={`Title (${TRANSLATION_LOCALES[translationTab].name})`}
                        value={translations[TRANSLATION_LOCALES[translationTab].code]?.title ?? ''}
                        onChange={(v) => updateTranslation(TRANSLATION_LOCALES[translationTab].code, 'title', v)}
                        autoComplete="off"
                        placeholder={title}
                      />
                      {questions.map((q, qIdx) => (
                        q.questionText.trim() ? (
                          <BlockStack key={qIdx} gap="200">
                            <Divider />
                            <Text as="p" variant="bodySm" tone="subdued">
                              Pregunta {qIdx + 1} (ES): {q.questionText}
                            </Text>
                            <TextField
                              label={`Pregunta ${qIdx + 1} (${TRANSLATION_LOCALES[translationTab].name})`}
                              value={translations[TRANSLATION_LOCALES[translationTab].code]?.[`question_${qIdx}`] ?? ''}
                              onChange={(v) => updateTranslation(TRANSLATION_LOCALES[translationTab].code, `question_${qIdx}`, v)}
                              autoComplete="off"
                              placeholder={q.questionText}
                            />
                            {q.options.filter((o) => o.trim()).map((opt, oIdx) => (
                              <TextField
                                key={oIdx}
                                label={`Opción ${oIdx + 1} (${TRANSLATION_LOCALES[translationTab].name})`}
                                value={translations[TRANSLATION_LOCALES[translationTab].code]?.[`question_${qIdx}_option_${oIdx}`] ?? ''}
                                onChange={(v) => updateTranslation(TRANSLATION_LOCALES[translationTab].code, `question_${qIdx}_option_${oIdx}`, v)}
                                autoComplete="off"
                                placeholder={opt}
                              />
                            ))}
                          </BlockStack>
                        ) : null
                      ))}
                    </BlockStack>
                  </Card>
                </Tabs>
              </BlockStack>
            </Collapsible>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
